import React, { useState, useMemo } from 'react';
import { FeedbackStatus } from '../Feedback';
import { TableDataProps } from '@/types/components';
import { SortDirection, Column } from '@/types/common';

export const TableData = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  sorting,
  selection,
  rowActions,
  emptyState,
  className = '',
  size = 'medium',
  onRowClick,
}: TableDataProps<T>) => {
  const [internalSorting, setInternalSorting] = useState<{
    field: string;
    direction: SortDirection;
  }>({
    field: sorting?.field || '',
    direction: sorting?.direction || null,
  });

  // Size variants
  const sizeClasses = {
    small: 'text-xs',
    medium: 'text-[14px]',
    large: 'text-base',
  };

  const cellPadding = {
    small: 'px-3 py-2',
    medium: 'px-6 py-4',
    large: 'px-8 py-6',
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const column = columns.find((col) => col.key === field);
    if (!column?.sortable) return;

    let newDirection: SortDirection = 'asc';

    if (internalSorting.field === field) {
      if (internalSorting.direction === 'asc') {
        newDirection = 'desc';
      } else if (internalSorting.direction === 'desc') {
        newDirection = null;
      }
    }

    const newSorting = { field, direction: newDirection };
    setInternalSorting(newSorting);
    sorting?.onSort?.(field, newDirection);
  };

  // Sort data if no external sorting
  const sortedData = useMemo(() => {
    if (sorting?.onSort || !internalSorting.direction) return data;

    return [...data].sort((a, b) => {
      const aValue = a[internalSorting.field];
      const bValue = b[internalSorting.field];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return internalSorting.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, internalSorting, sorting?.onSort]);

  // Handle selection
  const handleSelectAll = (checked: boolean) => {
    if (!selection?.onSelectionChange || !selection?.getRowKey) return;

    if (checked) {
      const allKeys = sortedData
        .filter((r) => selection.isRowSelectable?.(r) !== false)
        .map(selection.getRowKey);
      selection.onSelectionChange(allKeys);
    } else {
      selection.onSelectionChange([]);
    }
  };

  const handleSelectRow = (record: T, checked: boolean) => {
    if (!selection?.onSelectionChange || !selection?.getRowKey) return;

    const key = selection.getRowKey(record);
    const newSelectedKeys = checked
      ? [...selection.selectedKeys, key]
      : selection.selectedKeys.filter((k) => k !== key);

    selection.onSelectionChange(newSelectedKeys);
  };

  // Render cell content
  const renderCell = (column: Column<T>, record: T, index: number) => {
    const value = record[column.key];

    if (column.render) {
      return column.render(value, record, index);
    }

    // Auto-render status badges
    if (
      column.key.toLowerCase().includes('status') &&
      typeof value === 'string'
    ) {
      return <FeedbackStatus status={value} />;
    }

    return value;
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 ${className}`}
      >
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded-t-lg"></div>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-16 bg-gray-100 border-t border-gray-200"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!data.length) {
    // Auto-detect search scenario by checking URL params or document title
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam =
      urlParams.get('search') || urlParams.get('query') || urlParams.get('q');

    // Also check for search input in the document
    const searchInput = document.querySelector(
      'input[placeholder*="Search"], input[placeholder*="search"], input[type="search"]'
    ) as HTMLInputElement;
    const searchTerm = searchParam || searchInput?.value || '';

    return (
      <div
        className={`bg-white rounded-lg border border-gray-200 ${className}`}
      >
        <div className="p-12 text-center">
          {emptyState || (
            <div>
              <div className="w-[148px] h-[148px] mx-auto mb-4">
                <img
                  src="/images/no-results.png"
                  alt="No results found"
                  className="w-full h-full"
                />
              </div>
              <h3
                className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] text-gray-900 mb-2 text-center"
                style={{ letterSpacing: '-1.5%' }}
              >
                {searchTerm
                  ? `No results found for "${searchTerm}"`
                  : 'No data'}
              </h3>
              <p
                className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] leading-[20px] text-gray-500 text-center"
                style={{ letterSpacing: '-0.6%' }}
              >
                {searchTerm
                  ? 'Try checking the spelling or using a different search term.'
                  : 'Try checking the spelling or using a different search term.'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Header */}
          <thead className="bg-gray-50">
            <tr>
              {/* Selection column */}
              {selection && (
                <th className={`${cellPadding[size]} text-left`}>
                  <input
                    type="checkbox"
                    checked={(() => {
                      const selectable = sortedData.filter(
                        (r) => selection.isRowSelectable?.(r) !== false
                      );
                      return (
                        selectable.length > 0 &&
                        selectable.every((r) =>
                          selection.selectedKeys.includes(
                            selection.getRowKey!(r)
                          )
                        )
                      );
                    })()}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                </th>
              )}

              {/* Data columns */}
              {columns.map((column, columnIndex) => (
                <th
                  key={column.key}
                  className={`${
                    cellPadding[size]
                  } text-left text-[14px] font-medium text-gray-500 tracking-wider whitespace-nowrap ${
                    column.key === 'actions'
                      ? 'sticky right-0 z-[1] bg-gray-50 shadow-left'
                      : column.sticky === 'right'
                        ? 'sticky right-0 bg-gray-50 border-l border-gray-200 z-10'
                        : column.sticky === 'left'
                          ? 'sticky left-0 bg-gray-50 border-r border-gray-200 z-10'
                          : ''
                  }`}
                  style={{ width: column.width }}
                >
                  <div
                    className={`flex items-center gap-1 ${
                      column.align === 'center'
                        ? 'justify-center'
                        : column.align === 'right'
                          ? 'justify-end'
                          : ''
                    }`}
                  >
                    <span>{column.title}</span>
                    {column.sortable && (
                      <button
                        onClick={() => handleSort(column.key)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <i
                          className={`text-[12px] ${
                            internalSorting.field === column.key
                              ? internalSorting.direction === 'asc'
                                ? 'ri-arrow-up-line'
                                : internalSorting.direction === 'desc'
                                  ? 'ri-arrow-down-line'
                                  : 'ri-expand-up-down-line'
                              : 'ri-expand-up-down-line'
                          }`}
                        ></i>
                      </button>
                    )}
                  </div>
                </th>
              ))}

              {/* Actions column */}
              {rowActions && (
                <th
                  className={`${cellPadding[size]} sticky right-0 bg-gray-50 border-l border-gray-200 z-10`}
                >
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.map((record, index) => {
              const rowKey = selection?.getRowKey?.(record) || index.toString();
              const isSelected =
                selection?.selectedKeys.includes(rowKey) || false;

              return (
                <tr
                  key={rowKey}
                  className={`hover:bg-gray-50 ${
                    isSelected ? 'bg-purple-50' : ''
                  } ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(record, index)}
                >
                  {/* Selection column */}
                  {selection && (
                    <td className={cellPadding[size]}>
                      {selection.isRowSelectable?.(record) !== false ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            handleSelectRow(record, e.target.checked)
                          }
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                      ) : null}
                    </td>
                  )}

                  {/* Data columns */}
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column.key}
                      className={`${cellPadding[size]} ${
                        sizeClasses[size]
                      } text-gray-900 whitespace-nowrap ${
                        column.align === 'center'
                          ? 'text-center'
                          : column.align === 'right'
                            ? 'text-right'
                            : ''
                      } ${
                        column.key === 'actions'
                          ? 'sticky right-0 z-[2] bg-white shadow-left'
                          : ''
                      }`}
                    >
                      {renderCell(column, record, index)}
                    </td>
                  ))}

                  {/* Actions column */}
                  {rowActions && (
                    <td
                      className={`${cellPadding[size]} sticky right-0 bg-white border-l border-gray-200 z-10 text-right ${sizeClasses[size]} font-medium`}
                    >
                      {rowActions.render(record, index)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableData;
